import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import Navbar from '../components/Navbar';
import {
    Bot, Play, Square, TrendingUp, TrendingDown, AlertTriangle,
    Clock, DollarSign, Target, CheckCircle, XCircle, RefreshCw,
    ChevronRight, Package, BarChart3, History, Settings,
    ArrowUpCircle, ArrowDownCircle, Wallet
} from 'lucide-react';

const BotTradingPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [packages, setPackages] = useState([]);
    const [settings, setSettings] = useState({});
    const [subscription, setSubscription] = useState(null);
    const [openTrades, setOpenTrades] = useState([]);
    const [topCryptos, setTopCryptos] = useState([]);
    const [selectedCryptos, setSelectedCryptos] = useState([]);
    const [investmentAmount, setInvestmentAmount] = useState('');
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [trades, setTrades] = useState([]);
    const [reports, setReports] = useState([]);
    const [reportSummary, setReportSummary] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [txSummary, setTxSummary] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);

            // Fetch packages and settings
            const [packagesRes, subscriptionRes, cryptosRes] = await Promise.all([
                api.get('/bot-trading/packages'),
                api.get('/bot-trading/subscription'),
                api.get('/bot-trading/top-cryptos')
            ]);

            setPackages(packagesRes.data.packages || []);
            setSettings(packagesRes.data.settings || {});
            setTopCryptos(cryptosRes.data.cryptos || []);

            if (subscriptionRes.data.subscription) {
                setSubscription(subscriptionRes.data.subscription);
                setOpenTrades(subscriptionRes.data.openTrades || []);
                setSelectedCryptos(subscriptionRes.data.subscription.selectedCryptos || []);
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Auto-refresh P&L every 5 seconds when bot is running
    useEffect(() => {
        if (!subscription?.isBotRunning) return;

        const refreshPnL = async () => {
            try {
                const subscriptionRes = await api.get('/bot-trading/subscription');
                if (subscriptionRes.data.subscription) {
                    setSubscription(subscriptionRes.data.subscription);
                    setOpenTrades(subscriptionRes.data.openTrades || []);
                }
            } catch (error) {
                console.error('Failed to refresh P&L:', error);
            }
        };

        const interval = setInterval(refreshPnL, 5000); // 5 seconds
        return () => clearInterval(interval);
    }, [subscription?.isBotRunning]);

    const fetchTrades = async () => {
        try {
            const res = await api.get('/bot-trading/trades');
            setTrades(res.data.trades || []);
        } catch (error) {
            console.error('Failed to fetch trades:', error);
        }
    };

    const fetchReports = async () => {
        try {
            const res = await api.get('/bot-trading/reports?days=30');
            setReports(res.data.reports || []);
            setReportSummary(res.data.summary);
        } catch (error) {
            console.error('Failed to fetch reports:', error);
        }
    };

    const fetchTransactions = async () => {
        try {
            const res = await api.get('/bot-trading/transactions');
            setTransactions(res.data.transactions || []);
            setTxSummary(res.data.summary);
        } catch (error) {
            console.error('Failed to fetch transactions:', error);
        }
    };

    useEffect(() => {
        if (activeTab === 'trades') fetchTrades();
        if (activeTab === 'reports') fetchReports();
        if (activeTab === 'transactions') fetchTransactions();
    }, [activeTab]);

    const handleSubscribe = async () => {
        if (!selectedPackage) {
            toast.error('Please select a package');
            return;
        }
        if (!investmentAmount || parseFloat(investmentAmount) < parseFloat(settings.minInvestment || 10)) {
            toast.error(`Minimum investment is $${settings.minInvestment || 10}`);
            return;
        }
        if (selectedCryptos.length === 0) {
            toast.error('Please select at least one cryptocurrency');
            return;
        }

        try {
            await api.post('/bot-trading/subscribe', {
                packageId: selectedPackage.id,
                investmentAmount: parseFloat(investmentAmount),
                selectedCryptos
            });
            toast.success('Subscription created successfully!');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to subscribe');
        }
    };

    const handleStartBot = async () => {
        try {
            await api.post('/bot-trading/start');
            toast.success('Bot started successfully!');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to start bot');
        }
    };

    const handleStopBot = async () => {
        try {
            await api.post('/bot-trading/stop');
            toast.success('Bot stopped');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to stop bot');
        }
    };

    const handleUpdateCryptos = async () => {
        try {
            await api.put('/bot-trading/cryptos', { selectedCryptos });
            toast.success('Cryptocurrencies updated');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to update');
        }
    };

    const toggleCrypto = (symbol) => {
        setSelectedCryptos(prev =>
            prev.includes(symbol)
                ? prev.filter(s => s !== symbol)
                : prev.length < 10 ? [...prev, symbol] : prev
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0b0e11]">
                <Navbar />
                <div className="flex items-center justify-center h-[calc(100vh-64px)]">
                    <RefreshCw className="animate-spin text-yellow-500" size={40} />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0b0e11]">
            <Navbar />

            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                            <Bot className="text-yellow-500" /> Spot Bot Trading
                        </h1>
                        <p className="text-gray-400 text-sm mt-1">
                            Automated trading with professional strategies
                        </p>
                    </div>
                    {subscription && (
                        <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-sm ${subscription.isBotRunning
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-gray-500/20 text-gray-400'
                                }`}>
                                {subscription.isBotRunning ? '● Running' : '○ Stopped'}
                            </span>
                        </div>
                    )}
                </div>

                {/* Expiry Warning */}
                {subscription?.isExpiringSoon && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
                        <AlertTriangle className="text-yellow-500" />
                        <div>
                            <p className="text-yellow-400 font-medium">Subscription Expiring Soon!</p>
                            <p className="text-yellow-400/70 text-sm">
                                Your subscription will expire in {subscription.daysRemaining} days on {new Date(subscription.endDate).toLocaleDateString()}
                            </p>
                        </div>
                    </div>
                )}

                {!subscription ? (
                    /* No Subscription - Show Packages */
                    <div className="space-y-8">
                        {/* Packages */}
                        <div>
                            <h2 className="text-xl font-semibold text-white mb-4">Choose Your Package</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {packages.map((pkg) => (
                                    <div
                                        key={pkg.id}
                                        onClick={() => setSelectedPackage(pkg)}
                                        className={`bg-slate-800/50 border rounded-xl p-6 cursor-pointer transition-all ${selectedPackage?.id === pkg.id
                                            ? 'border-yellow-500 ring-2 ring-yellow-500/20'
                                            : 'border-slate-700 hover:border-slate-600'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-bold text-white">{pkg.name}</h3>
                                            <Package className={selectedPackage?.id === pkg.id ? 'text-yellow-500' : 'text-gray-500'} />
                                        </div>
                                        <div className="text-3xl font-bold text-yellow-500 mb-2">
                                            ${parseFloat(pkg.price).toFixed(0)}
                                            <span className="text-sm text-gray-400 font-normal">/month</span>
                                        </div>
                                        <p className="text-gray-400 text-sm mb-4">{pkg.description}</p>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-400">Daily Profit</span>
                                                <span className="text-green-400">
                                                    {pkg.minDailyProfitPercent}% - {pkg.maxDailyProfitPercent}%
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-400">Max Investment</span>
                                                <span className="text-white">${parseFloat(pkg.maxInvestment).toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-400">Duration</span>
                                                <span className="text-white">{pkg.durationDays} days</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Investment Amount */}
                        {selectedPackage && (
                            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                                <h3 className="text-lg font-semibold text-white mb-4">Investment Amount</h3>
                                <div className="flex items-center gap-4">
                                    <div className="flex-1">
                                        <input
                                            type="number"
                                            value={investmentAmount}
                                            onChange={(e) => setInvestmentAmount(e.target.value)}
                                            placeholder={`Min $${settings.minInvestment || 10}`}
                                            className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white"
                                        />
                                    </div>
                                    <span className="text-gray-400">USDT</span>
                                </div>
                                <p className="text-gray-500 text-sm mt-2">
                                    Total: ${(parseFloat(selectedPackage.price) + (parseFloat(investmentAmount) || 0)).toFixed(2)} (Package + Investment)
                                </p>
                            </div>
                        )}

                        {/* Select Cryptos */}
                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-white">Select Cryptocurrencies (Max 10)</h3>
                                <span className="text-yellow-500">{selectedCryptos.length}/10 selected</span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                {topCryptos.map((crypto) => (
                                    <div
                                        key={crypto.symbol}
                                        onClick={() => toggleCrypto(crypto.symbol)}
                                        className={`p-3 rounded-lg cursor-pointer transition-all ${selectedCryptos.includes(crypto.symbol)
                                            ? 'bg-yellow-500/20 border border-yellow-500'
                                            : 'bg-slate-700/50 border border-slate-600 hover:border-slate-500'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-white font-medium text-sm">
                                                {crypto.symbol.replace('USDT', '')}
                                            </span>
                                            {selectedCryptos.includes(crypto.symbol) && (
                                                <CheckCircle size={16} className="text-yellow-500" />
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-400 mt-1">
                                            ${parseFloat(crypto.price).toLocaleString()}
                                        </div>
                                        <div className={`text-xs mt-1 ${crypto.priceChange >= 0 ? 'text-green-400' : 'text-red-400'
                                            }`}>
                                            {crypto.priceChange >= 0 ? '+' : ''}{crypto.priceChange?.toFixed(2)}%
                                        </div>
                                        <div className="text-xs text-green-400 mt-1">
                                            ~{crypto.last10Days?.avgDailyProfit || '0.50'}%/day
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Subscribe Button */}
                        <button
                            onClick={handleSubscribe}
                            disabled={!selectedPackage || !investmentAmount || selectedCryptos.length === 0}
                            className="w-full py-4 bg-yellow-500 text-black font-bold rounded-lg hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            Subscribe & Start Bot Trading
                        </button>
                    </div>
                ) : (
                    /* Active Subscription - Dashboard */
                    <div>
                        {/* Tabs */}
                        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-2">
                            {['dashboard', 'trades', 'transactions', 'reports', 'settings'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 py-2 rounded-lg capitalize transition-all ${activeTab === tab
                                        ? 'bg-yellow-500 text-black font-medium'
                                        : 'text-gray-400 hover:text-white'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {activeTab === 'dashboard' && (
                            <div className="space-y-6">
                                {/* Stats */}
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                        <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                                            <Package size={16} /> Package
                                        </div>
                                        <div className="text-xl font-bold text-white">
                                            {subscription.package?.name || 'Unknown'}
                                        </div>
                                    </div>
                                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                        <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                                            <DollarSign size={16} /> Price
                                        </div>
                                        <div className="text-xl font-bold text-yellow-500">
                                            ${parseFloat(subscription.package?.price || 0).toFixed(0)}
                                        </div>
                                    </div>
                                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                        <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                                            <DollarSign size={16} /> Investment
                                        </div>
                                        <div className="text-xl font-bold text-white">
                                            ${parseFloat(subscription.investmentAmount).toFixed(2)}
                                        </div>
                                    </div>
                                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                        <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                                            <TrendingUp size={16} /> Total Profit
                                        </div>
                                        <div className={`text-xl font-bold ${parseFloat(subscription.totalProfit) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {parseFloat(subscription.totalProfit) >= 0 ? '+' : ''}${parseFloat(subscription.totalProfit).toFixed(4)}
                                        </div>
                                    </div>
                                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                        <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                                            <Target size={16} /> Win Rate
                                        </div>
                                        <div className="text-xl font-bold text-white">
                                            {subscription.winRate}%
                                        </div>
                                    </div>
                                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                        <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                                            <Clock size={16} /> Days Left
                                        </div>
                                        <div className="text-xl font-bold text-white">
                                            {subscription.daysRemaining}
                                        </div>
                                    </div>
                                </div>

                                {/* Bot Control */}
                                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-lg font-semibold text-white">Bot Control</h3>
                                            <p className="text-gray-400 text-sm">
                                                Current Balance: ${parseFloat(subscription.currentBalance).toFixed(2)} USDT
                                            </p>
                                        </div>
                                        {subscription.isBotRunning ? (
                                            <button
                                                onClick={handleStopBot}
                                                className="flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-400 transition-all"
                                            >
                                                <Square size={18} /> Stop Bot
                                            </button>
                                        ) : (
                                            <button
                                                onClick={handleStartBot}
                                                className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-400 transition-all"
                                            >
                                                <Play size={18} /> Start Bot
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Open Trades */}
                                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4">Open Positions</h3>
                                    {openTrades.length === 0 ? (
                                        <p className="text-gray-400 text-center py-8">No open positions</p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="text-gray-400 text-sm border-b border-slate-700">
                                                        <th className="text-left pb-3 pr-4 whitespace-nowrap">Pair</th>
                                                        <th className="text-left pb-3 pr-4 whitespace-nowrap">Strategy</th>
                                                        <th className="text-right pb-3 pr-4 whitespace-nowrap">Entry Price</th>
                                                        <th className="text-right pb-3 pr-4 whitespace-nowrap">Qty</th>
                                                        <th className="text-right pb-3 pr-4 whitespace-nowrap">Amount</th>
                                                        <th className="text-left pb-3 pr-4 whitespace-nowrap">Entry Time</th>
                                                        <th className="text-right pb-3 whitespace-nowrap">P&L</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {openTrades.map((trade) => (
                                                        <tr key={trade.id} className="border-b border-slate-700/50">
                                                            <td className="py-3 pr-4 text-white font-medium whitespace-nowrap">{trade.symbol}</td>
                                                            <td className="py-3 pr-4 text-gray-400 text-sm whitespace-nowrap">{trade.strategy}</td>
                                                            <td className="py-3 pr-4 text-right text-white whitespace-nowrap">
                                                                ${parseFloat(trade.entryPrice).toFixed(4)}
                                                            </td>
                                                            <td className="py-3 pr-4 text-right text-white whitespace-nowrap">
                                                                {parseFloat(trade.quantity).toFixed(6)}
                                                            </td>
                                                            <td className="py-3 pr-4 text-right text-white whitespace-nowrap">
                                                                ${parseFloat(trade.amountUsdt).toFixed(2)}
                                                            </td>
                                                            <td className="py-3 pr-4 text-left text-gray-400 text-sm whitespace-nowrap">
                                                                {new Date(trade.createdAt).toLocaleString()}
                                                            </td>
                                                            <td className={`py-3 text-right font-medium whitespace-nowrap ${parseFloat(trade.unrealizedPnL) >= 0 ? 'text-green-400' : 'text-red-400'
                                                                }`}>
                                                                {parseFloat(trade.unrealizedPnL) >= 0 ? '+' : ''}
                                                                ${trade.unrealizedPnL} ({trade.unrealizedPnLPercent}%)
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'trades' && (
                            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                                <h3 className="text-lg font-semibold text-white mb-4">Trade History</h3>
                                {trades.length === 0 ? (
                                    <p className="text-gray-400 text-center py-8">No trades yet</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="text-gray-400 text-sm border-b border-slate-700">
                                                    <th className="text-left pb-3 pr-4 whitespace-nowrap">Pair</th>
                                                    <th className="text-left pb-3 pr-4 whitespace-nowrap">Strategy</th>
                                                    <th className="text-right pb-3 pr-4 whitespace-nowrap">Qty</th>
                                                    <th className="text-right pb-3 pr-4 whitespace-nowrap">Entry Price</th>
                                                    <th className="text-left pb-3 pr-4 whitespace-nowrap">Entry Time</th>
                                                    <th className="text-right pb-3 pr-4 whitespace-nowrap">Exit Price</th>
                                                    <th className="text-left pb-3 pr-4 whitespace-nowrap">Exit Time</th>
                                                    <th className="text-right pb-3 pr-4 whitespace-nowrap">PNL</th>
                                                    <th className="text-center pb-3 whitespace-nowrap">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {trades.map((trade) => (
                                                    <tr key={trade.id} className="border-b border-slate-700/50">
                                                        <td className="py-3 pr-4 text-white font-medium whitespace-nowrap">{trade.symbol}</td>
                                                        <td className="py-3 pr-4 text-gray-400 text-sm whitespace-nowrap">{trade.strategy}</td>
                                                        <td className="py-3 pr-4 text-right text-white whitespace-nowrap">
                                                            {parseFloat(trade.quantity).toFixed(6)}
                                                        </td>
                                                        <td className="py-3 pr-4 text-right text-white whitespace-nowrap">
                                                            ${trade.entryPrice?.toFixed(4)}
                                                        </td>
                                                        <td className="py-3 pr-4 text-left text-gray-400 text-sm whitespace-nowrap">
                                                            {new Date(trade.createdAt).toLocaleString()}
                                                        </td>
                                                        <td className="py-3 pr-4 text-right text-white whitespace-nowrap">
                                                            {trade.exitPrice ? `$${trade.exitPrice.toFixed(4)}` : '-'}
                                                        </td>
                                                        <td className="py-3 pr-4 text-left text-gray-400 text-sm whitespace-nowrap">
                                                            {trade.closedAt ? new Date(trade.closedAt).toLocaleString() : '-'}
                                                        </td>
                                                        <td className={`py-3 pr-4 text-right font-medium whitespace-nowrap ${trade.userProfit >= 0 ? 'text-green-400' : 'text-red-400'
                                                            }`}>
                                                            {trade.userProfit >= 0 ? '+' : ''}${trade.userProfit?.toFixed(4)}
                                                        </td>
                                                        <td className="py-3 text-center whitespace-nowrap">
                                                            <span className={`px-2 py-1 rounded text-xs ${trade.status === 'closed'
                                                                ? 'bg-green-500/20 text-green-400'
                                                                : trade.status === 'open'
                                                                    ? 'bg-blue-500/20 text-blue-400'
                                                                    : 'bg-yellow-500/20 text-yellow-400'
                                                                }`}>
                                                                {trade.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'reports' && (
                            <div className="space-y-6">
                                {reportSummary && (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                            <div className="text-gray-400 text-sm mb-1">Total Trades</div>
                                            <div className="text-xl font-bold text-white">{reportSummary.totalTrades}</div>
                                        </div>
                                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                            <div className="text-gray-400 text-sm mb-1">Win Rate</div>
                                            <div className="text-xl font-bold text-white">{reportSummary.overallWinRate}%</div>
                                        </div>
                                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                            <div className="text-gray-400 text-sm mb-1">Net Profit</div>
                                            <div className="text-xl font-bold text-green-400">+${reportSummary.totalNetProfit}</div>
                                        </div>
                                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                            <div className="text-gray-400 text-sm mb-1">Avg Daily</div>
                                            <div className="text-xl font-bold text-white">${reportSummary.avgDailyProfit}</div>
                                        </div>
                                    </div>
                                )}

                                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4">Daily Reports</h3>
                                    {reports.length === 0 ? (
                                        <p className="text-gray-400 text-center py-8">No reports yet</p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="text-gray-400 text-sm border-b border-slate-700">
                                                        <th className="text-left pb-3">Date</th>
                                                        <th className="text-right pb-3">Trades</th>
                                                        <th className="text-right pb-3">Win Rate</th>
                                                        <th className="text-right pb-3">Gross Profit</th>
                                                        <th className="text-right pb-3">Exchange Fee</th>
                                                        <th className="text-right pb-3">Net Profit</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reports.map((report, idx) => (
                                                        <tr key={idx} className="border-b border-slate-700/50">
                                                            <td className="py-3 text-white">{report.date}</td>
                                                            <td className="py-3 text-right text-white">{report.tradesCount}</td>
                                                            <td className="py-3 text-right text-white">{report.winRate}%</td>
                                                            <td className="py-3 text-right text-white">${report.grossProfit}</td>
                                                            <td className="py-3 text-right text-yellow-400">-${report.exchangeShare}</td>
                                                            <td className="py-3 text-right text-green-400 font-medium">+${report.netProfit}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'transactions' && (
                            <div className="space-y-6">
                                {/* Summary Cards */}
                                {txSummary && (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                                                <Wallet size={16} /> Investment
                                            </div>
                                            <div className="text-xl font-bold text-white">
                                                ${txSummary.investmentAmount?.toFixed(2)}
                                            </div>
                                        </div>
                                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                                                <TrendingUp size={16} /> Total Profit
                                            </div>
                                            <div className={`text-xl font-bold ${txSummary.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                {txSummary.totalProfit >= 0 ? '+' : ''}${txSummary.totalProfit?.toFixed(4)}
                                            </div>
                                        </div>
                                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                                                <Clock size={16} /> Locked in Trades
                                            </div>
                                            <div className="text-xl font-bold text-yellow-400">
                                                ${txSummary.lockedInTrades?.toFixed(2)}
                                            </div>
                                            <div className="text-xs text-gray-500">{txSummary.openTradesCount} open trades</div>
                                        </div>
                                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                                                <DollarSign size={16} /> Current Balance
                                            </div>
                                            <div className="text-xl font-bold text-white">
                                                ${txSummary.currentBalance?.toFixed(2)}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Balance Explanation */}
                                {txSummary && (
                                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                                        <p className="text-blue-400 text-sm">
                                            <strong>Balance Calculation:</strong> Investment (${txSummary.investmentAmount?.toFixed(2)})
                                            + Profit/Loss ({txSummary.totalProfit >= 0 ? '+' : ''}${txSummary.totalProfit?.toFixed(4)})
                                            - Locked in Open Trades (${txSummary.lockedInTrades?.toFixed(2)})
                                            = <strong>Current Balance (${txSummary.currentBalance?.toFixed(2)})</strong>
                                        </p>
                                    </div>
                                )}

                                {/* Transactions Table */}
                                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4">Fund Movements</h3>
                                    {transactions.length === 0 ? (
                                        <p className="text-gray-400 text-center py-8">No transactions yet</p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="text-gray-400 text-sm border-b border-slate-700">
                                                        <th className="text-left pb-3">Date</th>
                                                        <th className="text-left pb-3">Type</th>
                                                        <th className="text-left pb-3">Description</th>
                                                        <th className="text-right pb-3">Amount</th>
                                                        <th className="text-right pb-3">Balance</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {transactions.map((tx) => (
                                                        <tr key={tx.id} className="border-b border-slate-700/50">
                                                            <td className="py-3 text-gray-400 text-sm">
                                                                {new Date(tx.date).toLocaleString()}
                                                            </td>
                                                            <td className="py-3">
                                                                <span className={`flex items-center gap-1 ${tx.type === 'credit' ? 'text-green-400' : 'text-red-400'}`}>
                                                                    {tx.type === 'credit' ? (
                                                                        <ArrowDownCircle size={14} />
                                                                    ) : (
                                                                        <ArrowUpCircle size={14} />
                                                                    )}
                                                                    {tx.type === 'credit' ? 'Credit' : 'Debit'}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 text-white text-sm">
                                                                {tx.description}
                                                            </td>
                                                            <td className={`py-3 text-right font-medium ${tx.type === 'credit' ? 'text-green-400' : 'text-red-400'}`}>
                                                                {tx.type === 'credit' ? '+' : '-'}${tx.amount?.toFixed(4)}
                                                            </td>
                                                            <td className="py-3 text-right text-white">
                                                                ${tx.runningBalance?.toFixed(2)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'settings' && (
                            <div className="space-y-6">
                                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4">Selected Cryptocurrencies</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
                                        {topCryptos.map((crypto) => (
                                            <div
                                                key={crypto.symbol}
                                                onClick={() => toggleCrypto(crypto.symbol)}
                                                className={`p-3 rounded-lg cursor-pointer transition-all ${selectedCryptos.includes(crypto.symbol)
                                                    ? 'bg-yellow-500/20 border border-yellow-500'
                                                    : 'bg-slate-700/50 border border-slate-600 hover:border-slate-500'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className="text-white font-medium text-sm">
                                                        {crypto.symbol.replace('USDT', '')}
                                                    </span>
                                                    {selectedCryptos.includes(crypto.symbol) && (
                                                        <CheckCircle size={16} className="text-yellow-500" />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        onClick={handleUpdateCryptos}
                                        className="px-6 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-400"
                                    >
                                        Save Changes
                                    </button>
                                </div>

                                {/* Manage Funds Section */}
                                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4">Manage Funds</h3>
                                    <p className="text-gray-400 text-sm mb-4">
                                        Current Bot Balance: <span className="text-yellow-500 font-bold">${parseFloat(subscription.currentBalance).toFixed(2)}</span>
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Add Funds */}
                                        <div className="bg-slate-900/50 rounded-lg p-4">
                                            <label className="text-sm text-gray-400 block mb-2">Add Funds from Spot Wallet</label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="number"
                                                    placeholder="Amount"
                                                    id="addFundsAmount"
                                                    className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                                                />
                                                <button
                                                    onClick={async () => {
                                                        const amount = document.getElementById('addFundsAmount').value;
                                                        if (!amount || parseFloat(amount) <= 0) {
                                                            toast.error('Enter valid amount');
                                                            return;
                                                        }
                                                        try {
                                                            await api.post('/bot-trading/add-funds', { amount: parseFloat(amount) });
                                                            toast.success(`$${amount} added to bot`);
                                                            document.getElementById('addFundsAmount').value = '';
                                                            fetchData();
                                                        } catch (error) {
                                                            toast.error(error.response?.data?.error || 'Failed to add funds');
                                                        }
                                                    }}
                                                    className="px-4 py-2 bg-green-500 text-white rounded font-medium hover:bg-green-400 transition-all"
                                                >
                                                    Add
                                                </button>
                                            </div>
                                        </div>
                                        {/* Withdraw Funds */}
                                        <div className="bg-slate-900/50 rounded-lg p-4">
                                            <label className="text-sm text-gray-400 block mb-2">Withdraw to Spot Wallet</label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="number"
                                                    placeholder="Amount"
                                                    id="withdrawFundsAmount"
                                                    className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                                                />
                                                <button
                                                    onClick={async () => {
                                                        const amount = document.getElementById('withdrawFundsAmount').value;
                                                        if (!amount || parseFloat(amount) <= 0) {
                                                            toast.error('Enter valid amount');
                                                            return;
                                                        }
                                                        try {
                                                            await api.post('/bot-trading/withdraw-funds', { amount: parseFloat(amount) });
                                                            toast.success(`$${amount} withdrawn to spot wallet`);
                                                            document.getElementById('withdrawFundsAmount').value = '';
                                                            fetchData();
                                                        } catch (error) {
                                                            toast.error(error.response?.data?.error || 'Failed to withdraw');
                                                        }
                                                    }}
                                                    className="px-4 py-2 bg-yellow-500 text-black rounded font-medium hover:bg-yellow-400 transition-all"
                                                >
                                                    Withdraw
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4">Subscription Details</h3>
                                    <div className="space-y-3 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Package</span>
                                            <span className="text-white">{subscription.package?.name}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Start Date</span>
                                            <span className="text-white">{new Date(subscription.startDate).toLocaleDateString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">End Date</span>
                                            <span className="text-white">{new Date(subscription.endDate).toLocaleDateString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Trading Fee</span>
                                            <span className="text-white">{settings.tradingFee}%</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Profit Share</span>
                                            <span className="text-white">{settings.exchangeProfitShare}% to exchange</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BotTradingPage;
